import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import type { CharacterImageKind } from "./types";
import {
  inspectCharacterImageFile,
  validateCharacterImageFile,
  type CharacterImageDraft,
  type ImageDimensionDecoder,
  type ImageDimensions,
} from "./character-image-upload";
import "./character-image-crop.css";

export interface CharacterImageCropSelection {
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export type CharacterImageCropper = (
  file: File,
  kind: CharacterImageKind,
  selection: CharacterImageCropSelection,
) => Promise<CharacterImageDraft>;

interface PendingCrop extends ImageDimensions {
  file: File;
  sourceUrl: string;
}

export interface CharacterImageUploadFieldProps {
  kind: CharacterImageKind;
  value: CharacterImageDraft | null;
  disabled?: boolean | undefined;
  decode?: ImageDimensionDecoder | undefined;
  crop?: CharacterImageCropper | undefined;
  onChange: (draft: CharacterImageDraft) => void;
  onBusyChange?: ((busy: boolean) => void) | undefined;
  onError?: ((message: string) => void) | undefined;
}

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function imageErrorMessage(cause: unknown): string {
  const code = cause instanceof Error ? cause.message : "";
  if (code === "character_image_too_large") return "图片不能超过 10 MiB。";
  if (code === "character_image_type_invalid") return "只支持 PNG、JPEG 或 WebP 图片。";
  if (code === "character_image_empty") return "图片文件为空，请重新选择。";
  return "无法读取或裁剪这张图片，请选择有效的 PNG、JPEG 或 WebP 文件。";
}

function cropSourceRect(
  dimensions: ImageDimensions,
  kind: CharacterImageKind,
  selection: CharacterImageCropSelection,
): { sx: number; sy: number; sw: number; sh: number } {
  const targetRatio = kind === "portrait" ? 9 / 21 : 1;
  const sourceRatio = dimensions.width / dimensions.height;
  const baseWidth = sourceRatio > targetRatio ? dimensions.height * targetRatio : dimensions.width;
  const baseHeight = sourceRatio > targetRatio ? dimensions.height : dimensions.width / targetRatio;
  const zoom = clamp(selection.zoom, ZOOM_MIN, ZOOM_MAX);
  const sw = baseWidth / zoom;
  const sh = baseHeight / zoom;
  const horizontalRoom = (dimensions.width - sw) / 2;
  const verticalRoom = (dimensions.height - sh) / 2;
  return {
    sx: horizontalRoom + clamp(selection.offsetX, -1, 1) * horizontalRoom,
    sy: verticalRoom + clamp(selection.offsetY, -1, 1) * verticalRoom,
    sw,
    sh,
  };
}

function cropPreviewStyle(
  dimensions: ImageDimensions,
  kind: CharacterImageKind,
  selection: CharacterImageCropSelection,
): CSSProperties {
  const targetRatio = kind === "portrait" ? 9 / 21 : 1;
  const sourceRatio = dimensions.width / dimensions.height;
  const zoom = clamp(selection.zoom, ZOOM_MIN, ZOOM_MAX);
  const widthInFrames = (sourceRatio > targetRatio ? sourceRatio / targetRatio : 1) * zoom;
  const heightInFrames = (sourceRatio > targetRatio ? 1 : targetRatio / sourceRatio) * zoom;
  const source = cropSourceRect(dimensions, kind, selection);
  return {
    width: `${widthInFrames * 100}%`,
    height: `${heightInFrames * 100}%`,
    left: `${-source.sx / dimensions.width * widthInFrames * 100}%`,
    top: `${-source.sy / dimensions.height * heightInFrames * 100}%`,
  };
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("character_image_crop_failed"));
    }, "image/webp", 0.92);
  });
}

function croppedFileName(originalName: string, mimeType: string): string {
  const base = originalName.replace(/\.[^.]+$/, "") || "character-image";
  const extension = mimeType === "image/png" ? "png" : mimeType === "image/jpeg" ? "jpg" : "webp";
  return `${base}-cropped.${extension}`;
}

export async function cropCharacterImageFile(
  file: File,
  kind: CharacterImageKind,
  selection: CharacterImageCropSelection,
): Promise<CharacterImageDraft> {
  const bitmap = await createImageBitmap(file);
  try {
    const source = cropSourceRect({ width: bitmap.width, height: bitmap.height }, kind, selection);
    const ratioUnit = kind === "portrait"
      ? Math.max(1, Math.min(100, Math.floor(Math.min(source.sw / 9, source.sh / 21))))
      : Math.max(1, Math.min(1400, Math.floor(Math.min(source.sw, source.sh))));
    const width = kind === "portrait" ? ratioUnit * 9 : ratioUnit;
    const height = kind === "portrait" ? ratioUnit * 21 : ratioUnit;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("character_image_crop_failed");
    context.drawImage(bitmap, source.sx, source.sy, source.sw, source.sh, 0, 0, width, height);
    const blob = await canvasBlob(canvas);
    const output = new File([blob], croppedFileName(file.name, blob.type), {
      type: blob.type,
      lastModified: Date.now(),
    });
    await validateCharacterImageFile(output, kind, async () => ({ width, height }));
    return { file: output, previewUrl: URL.createObjectURL(output), width, height };
  } finally {
    bitmap.close();
  }
}

export function CharacterImageUploadField({
  kind,
  value,
  disabled = false,
  decode,
  crop = cropCharacterImageFile,
  onChange,
  onBusyChange,
  onError,
}: CharacterImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pendingRef = useRef<PendingCrop | null>(null);
  const generationRef = useRef(0);
  const dragRef = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const [pending, setPending] = useState<PendingCrop | null>(null);
  const [validating, setValidating] = useState(false);
  const [cropping, setCropping] = useState(false);
  const [error, setError] = useState("");
  const [selection, setSelection] = useState<CharacterImageCropSelection>({ zoom: 1, offsetX: 0, offsetY: 0 });
  const label = kind === "portrait" ? "立绘" : "封图";

  function reportError(message: string): void {
    setError(message);
    onError?.(message);
  }

  function replacePending(next: PendingCrop | null): void {
    const current = pendingRef.current;
    if (current && current !== next) URL.revokeObjectURL(current.sourceUrl);
    pendingRef.current = next;
    setPending(next);
  }

  useEffect(() => {
    onBusyChange?.(validating || cropping || pending !== null);
  }, [cropping, onBusyChange, pending, validating]);

  useEffect(() => () => {
    generationRef.current += 1;
    const current = pendingRef.current;
    if (current) URL.revokeObjectURL(current.sourceUrl);
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (!pending) return;
    const dialog = dialogRef.current;
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (dialog && !dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute("open", "");
      }
    }
    dialog?.querySelector<HTMLElement>(".character-crop-frame")?.focus();
    return () => {
      if (dialog?.open) dialog.close();
      restoreTarget?.focus();
    };
  }, [pending]);

  async function chooseFile(file: File): Promise<void> {
    if (disabled || cropping) return;
    const generation = ++generationRef.current;
    replacePending(null);
    setSelection({ zoom: 1, offsetX: 0, offsetY: 0 });
    setValidating(true);
    reportError("");
    try {
      const inspected = await inspectCharacterImageFile(file, kind, decode);
      if (generation !== generationRef.current) return;
      const { needsCrop, ...dimensions } = inspected;
      if (needsCrop) {
        replacePending({ file, sourceUrl: URL.createObjectURL(file), ...dimensions });
        return;
      }
      onChange({ file, previewUrl: URL.createObjectURL(file), ...dimensions });
    } catch (cause) {
      if (generation === generationRef.current) reportError(imageErrorMessage(cause));
    } finally {
      if (generation === generationRef.current) setValidating(false);
    }
  }

  function cancelCrop(): void {
    if (cropping) return;
    generationRef.current += 1;
    replacePending(null);
    setSelection({ zoom: 1, offsetX: 0, offsetY: 0 });
  }

  async function confirmCrop(): Promise<void> {
    const target = pendingRef.current;
    if (!target || cropping) return;
    const generation = generationRef.current;
    setCropping(true);
    reportError("");
    try {
      const draft = await crop(target.file, kind, selection);
      if (generation !== generationRef.current || pendingRef.current !== target) {
        URL.revokeObjectURL(draft.previewUrl);
        return;
      }
      replacePending(null);
      onChange(draft);
    } catch (cause) {
      if (generation === generationRef.current) reportError(imageErrorMessage(cause));
    } finally {
      if (generation === generationRef.current) setCropping(false);
    }
  }

  function moveSelection(deltaX: number, deltaY: number): void {
    setSelection((current) => ({
      ...current,
      offsetX: clamp(current.offsetX + deltaX, -1, 1),
      offsetY: clamp(current.offsetY + deltaY, -1, 1),
    }));
  }

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>): void {
    if (cropping) return;
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function drag(event: ReactPointerEvent<HTMLButtonElement>): void {
    const current = dragRef.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const width = Math.max(1, event.currentTarget.clientWidth);
    const height = Math.max(1, event.currentTarget.clientHeight);
    moveSelection(-(event.clientX - current.x) * 2 / width, -(event.clientY - current.y) * 2 / height);
    dragRef.current = { pointerId: current.pointerId, x: event.clientX, y: event.clientY };
  }

  return <div className="character-image-upload-field">
    <div className={`character-image-stage ${kind}`}>
      {value ? <button type="button" className="character-image-picker" disabled={disabled || validating || cropping} aria-label={`选择角色${label}`} onClick={() => inputRef.current?.click()}><img src={value.previewUrl} alt={`新角色${label}草稿`} /></button>
        : <button type="button" className="character-image-empty" disabled={disabled || validating || cropping} aria-label={`选择角色${label}`} onClick={() => inputRef.current?.click()}><span>{validating ? "正在校验图片…" : `选择${label}`}</span></button>}
    </div>
    <input ref={inputRef} hidden type="file" accept="image/png,image/jpeg,image/webp" disabled={disabled || validating || cropping} onChange={(event) => {
      const file = event.currentTarget.files?.[0];
      event.currentTarget.value = "";
      if (file) void chooseFile(file);
    }} />
    <p className="character-image-hint">支持 PNG、JPEG、WebP，最大 10 MiB；{label}比例 {kind === "portrait" ? "9:21" : "1:1"}。比例不符时可移动并缩放裁剪。</p>
    {value ? <p className="character-image-draft">已选择 {value.width} × {value.height}，返回上一步不会丢失。</p> : null}
    {error ? <p className="action-error" role="alert">{error}</p> : null}

    {pending ? <div className="character-crop-backdrop" role="presentation">
      <dialog
        ref={dialogRef}
        className="character-crop-dialog"
        aria-labelledby="character-crop-title"
        onCancel={(event) => { event.preventDefault(); cancelCrop(); }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            cancelCrop();
            return;
          }
          if (event.key !== "Tab" || !dialogRef.current) return;
          const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])")];
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (!first || !last) { event.preventDefault(); return; }
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }}
      >
        <header><div><h3 id="character-crop-title">裁剪{label}</h3><p>拖动图片调整位置，使用滑块缩放；裁剪比例固定为 {kind === "portrait" ? "9:21" : "1:1"}。</p></div><button type="button" aria-label="关闭裁剪" disabled={cropping} onClick={cancelCrop}>×</button></header>
        <button
          type="button"
          className={`character-crop-frame ${kind}`}
          style={{ aspectRatio: kind === "portrait" ? "9 / 21" : "1 / 1" }}
          aria-label={`移动${label}裁剪区域`}
          onPointerDown={startDrag}
          onPointerMove={drag}
          onPointerUp={() => { dragRef.current = null; }}
          onPointerCancel={() => { dragRef.current = null; }}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 0.1 : 0.025;
            if (event.key === "ArrowLeft") moveSelection(-step, 0);
            else if (event.key === "ArrowRight") moveSelection(step, 0);
            else if (event.key === "ArrowUp") moveSelection(0, -step);
            else if (event.key === "ArrowDown") moveSelection(0, step);
            else return;
            event.preventDefault();
          }}
        >
          <img
            src={pending.sourceUrl}
            alt="待裁剪原图"
            draggable={false}
            style={cropPreviewStyle(pending, kind, selection)}
          />
          <span className="character-crop-grid" aria-hidden="true" />
        </button>
        <label className="character-crop-zoom">缩放图片<input aria-label="缩放图片" type="range" min={ZOOM_MIN} max={ZOOM_MAX} step="0.01" value={selection.zoom} disabled={cropping} onChange={(event) => setSelection((current) => ({ ...current, zoom: Number(event.target.value) }))} /></label>
        <div className="character-crop-actions"><button className="secondary outlined" type="button" data-cancel-crop disabled={cropping} onClick={cancelCrop}>取消</button><button className="primary" type="button" data-confirm-crop disabled={cropping} onClick={() => void confirmCrop()}>{cropping ? "裁剪中…" : "使用裁剪结果"}</button></div>
      </dialog>
    </div> : null}
  </div>;
}
